import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Box, 
  MenuItem, 
  IconButton, 
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CodeIcon from '@mui/icons-material/Code';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScienceIcon from '@mui/icons-material/Science';
import apiService from '../services/api';

interface Mapping {
  id: string;
  specimenPartId: string;
  specimenPart: {
    id: string;
    partName: string;
    description: string;
  };
  code: string;
  codeType: string;
  description: string;
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface SpecimenPart {
  id: string;
  partName: string;
  description: string;
}

const defaultForm: Partial<Mapping> = { 
  specimenPartId: '', 
  code: '', 
  codeType: '', 
  description: '',
  active: true
};

const SpecimenMapping: React.FC = () => {
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [parts, setParts] = useState<SpecimenPart[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState<Partial<Mapping>>(defaultForm);
  const [editing, setEditing] = useState<Mapping | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const data = await apiService.getSpecimenPartsCodesMapping(1, 1000);
      // Ensure data is an array, handle various response formats
      const mappingsArray = Array.isArray(data) ? data : (data?.data || data?.mappings || []);
      console.log('Raw mappings data:', mappingsArray);
      // Clean up any potential HTML entities or unwanted characters in codeType
      const cleanedMappings = mappingsArray.map((mapping: any) => ({
        ...mapping,
        codeType: mapping.codeType ? mapping.codeType.replace(/[<>]/g, '').trim() : mapping.codeType
      }));
      setMappings(cleanedMappings);
    } catch (error) {
      console.error('Error fetching specimen parts codes mapping:', error);
      setMappings([]); // Set to empty array on error
    }
    setLoading(false);
  };

  const fetchParts = async () => {
    try {
      const data = await apiService.getSpecimenParts();
      setParts(data);
    } catch (error) {
      console.error('Error fetching specimen parts:', error);
    }
  };

  useEffect(() => { fetchMappings(); fetchParts(); }, []);

  const handleOpenDialog = () => {
    setForm(defaultForm);
    setEditing(null);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => { setOpenDialog(false); setForm(defaultForm); };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleSave = async () => {
    try {
      if (editing) {
        await apiService.updateSpecimenPartsCodesMapping(editing.id, form);
      } else {
        await apiService.createSpecimenPartsCodesMapping(form);
      }
      handleCloseDialog();
      fetchMappings();
    } catch (error) {
      console.error('Error saving specimen parts codes mapping:', error);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await apiService.deleteSpecimenPartsCodesMapping(id);
      fetchMappings();
    } catch (error) {
      console.error('Error deleting specimen parts codes mapping:', error);
    }
  };
  const handleEdit = (row: Mapping) => {
    setForm({
      specimenPartId: row.specimenPartId,
      code: row.code,
      codeType: row.codeType,
      description: row.description,
      active: row.active
    });
    setEditing(row);
    setOpenDialog(true);
  };

  const filteredMappings = (Array.isArray(mappings) ? mappings : []).filter(mapping =>
    mapping.code.toLowerCase().includes(search.toLowerCase()) ||
    mapping.codeType.toLowerCase().includes(search.toLowerCase()) ||
    (mapping.specimenPart?.partName || '').toLowerCase().includes(search.toLowerCase()) ||
    (mapping.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginate the filtered mappings
  const paginatedMappings = filteredMappings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Specimen Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Map specimen parts to medical codes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Search Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by code, code type, specimen part, or description..."
            value={search}
            onChange={handleSearchChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: '#666' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Mappings Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Specimen Part
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Code Type
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Loading mappings...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please wait while we fetch the data
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paginatedMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <ScienceIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {search ? 'No mappings found' : 'No mappings available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {search 
                          ? 'Try adjusting your search terms or clear the search to see all mappings.'
                          : 'Get started by adding your first specimen mapping.'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMappings.map((mapping) => (
                  <TableRow 
                    key={mapping.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8f9fa',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ScienceIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {mapping.specimenPart?.partName || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={mapping.code} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CodeIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {mapping.codeType || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {mapping.description || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {mapping.active ? (
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                        ) : (
                          <CancelIcon sx={{ fontSize: 16, color: '#f44336' }} />
                        )}
                        <Typography variant="body2" sx={{ 
                          color: mapping.active ? '#4caf50' : '#f44336',
                          fontWeight: 500
                        }}>
                          {mapping.active ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          onClick={() => handleEdit(mapping)}
                          size="small"
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { 
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(mapping.id)}
                          size="small"
                          sx={{ 
                            color: '#d32f2f',
                            '&:hover': { 
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredMappings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: '#666'
              }
            }}
          />
        </Box>
      </Card>

      {/* Mapping Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editing ? 'Edit Mapping' : 'Add New Mapping'}
        </DialogTitle>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Specimen Part"
                  name="specimenPartId"
                  value={form.specimenPartId || ''}
                  onChange={handleFormChange}
                  required
                  disabled={!!editing}
                  sx={{ mb: 2 }}
                >
                  {parts.map(part => (
                    <MenuItem key={part.id} value={part.id}>{part.partName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Code Type"
                  name="codeType"
                  value={form.codeType || ''}
                  onChange={handleFormChange}
                  required
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="CPT">CPT</MenuItem>
                  <MenuItem value="SNOMED">SNOMED</MenuItem>
                  <MenuItem value="ICD-10">ICD-10</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Code"
                  name="code"
                  value={form.code}
                  onChange={handleFormChange}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={form.description || ''}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      name="active"
                      checked={form.active}
                      onChange={handleFormChange}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};
export default SpecimenMapping; 